import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import { startOfWeek, getDay, addDays, setHours, setMinutes } from 'date-fns';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import es from 'date-fns/locale/es';
import { getLocationSchedule } from '../../../services/LocationService';
import { getAllStaffSchedule } from '../../../services/UserService';
import DeleteEventModal from './DeleteEventModal';
import EditAddEventModal from './EditAddEventModal';
import { Fab } from '@mui/material';
import { useTranslation } from 'react-i18next';
import './Staff.scss';
import { getIcon } from '../../../utils/IconHandler';
import { getAssignedUsers } from '../../../services/LocationService';
import { scheduleType } from '../../../utils/Enums';

const StaffCalendar = () => {

    const { t } = useTranslation();
    const { locationId } = useParams();
    const [events, setEvents] = useState([]);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [minTime, setMinTime] = useState(new Date());
    const [maxTime, setMaxTime] = useState(new Date());
    const [minDate, setMinDate] = useState(null);
    const [maxDate, setMaxDate] = useState(null);
    const [users, setUsers] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditAddModalOpen, setIsEditAddModalOpen] = useState(false);

    const locales = {
      'es': es
    };

    const localizer = dateFnsLocalizer({
      format: (date, formatString) => format(date, formatString, { locale: es }),
      parse: parseISO,
      startOfWeek: () => startOfWeek(new Date(), { locale: es }),
      getDay: (date) => getDay(date, { locale: es }),
      locales
    });

    const handleSelectEvent = (event) => {
        if (event.type === 'staff') {
            setSelectedEvent(event);
            setIsModalOpen(true);
        } else {            
            const preloadedEvent = {
                date: event.start,
            };
            setSelectedEvent(preloadedEvent);
            openEditAddModal();
        }
    };

    const closeModal = () => {
        setSelectedEvent(null);
        setIsModalOpen(false);
    };

    useEffect(() => {
        // Fetch users when the component mounts or locationId changes
        const fetchUsers = async () => {
          try {
            const usersData = await getAssignedUsers(locationId);
            setUsers(usersData);
          } catch (error) {
            console.error('Error fetching users:', error);
          }
        };
    
        fetchUsers();
    }, [locationId]);

    useEffect(() => {
        const initialRange = calculateInitialRange();
        handleRangeChange(initialRange);
    }, []);

    useEffect(() => {
        if (minDate && maxDate) {
            fetchSchedule();
        }
    }, [locationId, currentDate, minDate, maxDate]);

    const calculateInitialRange = () => {
        const startOfThisWeek = startOfWeek(new Date(), { weekStartsOn: 1 });
        const endOfThisWeek = addDays(startOfThisWeek, 6);
        return { start: startOfThisWeek, end: endOfThisWeek };
    };

    const fetchSchedule = async () => {
        try {
            const locationSchedule = await getLocationSchedule(locationId);
            const staffSchedule = await getAllStaffSchedule(locationId, minDate, maxDate);
            updateEvents(locationSchedule, staffSchedule, currentDate);
        } catch (error) {
            console.error('Error fetching schedule:', error);
        }
    };

    const updateEvents = (locationSchedule, staffSchedule, date) => {
        const startOfThisWeek = startOfWeek(date, { weekStartsOn: 1 });
        let minHour = 24;
        let maxHour = 0;
    
        const parseTime = (timeStr) => {
            const [hour, minute] = timeStr.split(':').map(Number);
            return { hour, minute };
        };
    
        // Eventos de location (regulares)
        const locationEvents = locationSchedule.map(schedule => {
            const startTime = parseTime(schedule.startHour);
            const endTime = parseTime(schedule.endHour);
    
            const adjustedDay = (schedule.dayOfWeek === 0 ? 7 : schedule.dayOfWeek) - 1;
            const startDate = addDays(startOfThisWeek, adjustedDay);
    
            const startDateTime = new Date(startDate);
            startDateTime.setHours(startTime.hour, startTime.minute, 0, 0);
            const endDateTime = new Date(startDate);
            endDateTime.setHours(endTime.hour, endTime.minute, 0, 0);
            if (endDateTime <= startDateTime) {
                endDateTime.setDate(endDateTime.getDate() + 1);
            }
    
            minHour = Math.min(minHour, startDateTime.getHours());
            maxHour = Math.max(maxHour, endDateTime.getHours());
    
            return {
                title: 'Atención al público',
                start: startDateTime,
                end: endDateTime,
                allDay: false,
                type: 'location'
            };
        });
    
        // Eventos de staff (regulares + excepciones)
        const staffEvents = staffSchedule.map(schedule => {
            const startTime = parseTime(schedule.startHour);
            const endTime = parseTime(schedule.endHour);
    
            const adjustedDay = (schedule.dayOfWeek === 0 ? 7 : schedule.dayOfWeek) - 1;
            const startDate = addDays(startOfThisWeek, adjustedDay);
    
            const startDateTime = new Date(startDate);
            startDateTime.setHours(startTime.hour, startTime.minute, 0, 0);
            const endDateTime = new Date(startDate);
            endDateTime.setHours(endTime.hour, endTime.minute, 0, 0);
            if (endDateTime <= startDateTime) {
                endDateTime.setDate(endDateTime.getDate() + 1);
            }
    
            minHour = Math.min(minHour, startDateTime.getHours());
            maxHour = Math.max(maxHour, endDateTime.getHours());
    
            const scheduleLabel = scheduleType.find(type => type.id === schedule.scheduleType)?.label || 'Desconocido';
    
            return {
                title: `${schedule.staffFirstName} ${schedule.staffLastName} (${scheduleLabel})`,
                start: startDateTime,
                end: endDateTime,
                allDay: false,
                type: 'staff',
                staffEmail: schedule.staffEmail,
                backgroundColor: schedule.color,
                scheduleType: schedule.scheduleType
            };
        });
    
        // Combinar todos los eventos
        const allEvents = [...locationEvents, ...staffEvents];
    
        // Actualizar estados del calendario
        setEvents(allEvents);
        setMinTime(setHours(setMinutes(new Date(), 0), minHour));
        setMaxTime(setHours(setMinutes(new Date(), 0), maxHour));
    };
    

    const handleRangeChange = (range) => {
        if (Array.isArray(range)) {
            setMinDate(range[0]);
            setMaxDate(range[range.length - 1]);
        } else {
            setMinDate(range.start);
            setMaxDate(range.end);
        }
        setCurrentDate(Array.isArray(range) ? range[0] : range.start);
    };

    const eventPropGetter = (event) => {
        let backgroundColor = '#3174ad'; // default
        let zIndex = 1;

        if (event.type === 'staff') {
            backgroundColor = event.backgroundColor || '#ffc107';
            zIndex = 2; // encima de location
        } else if (event.type === 'location') {
            backgroundColor = '#90c3f2';
            zIndex = 1; // más atrás
        }

        return {
            style: {
                backgroundColor,
                zIndex,
            },
        };
    };

    const openEditAddModal = () => {
        setIsEditAddModalOpen(true);
    };

    const closeEditAddModal = () => {
        setIsEditAddModalOpen(false);
    };

    return (
        <>
            <Calendar
                localizer={localizer}
                events={events}
                defaultView="week"
                onRangeChange={handleRangeChange}
                onSelectEvent={handleSelectEvent}
                eventPropGetter={eventPropGetter}
                startAccessor="start"
                endAccessor="end"
                min={minTime}
                max={maxTime}
                messages={{
                    next: t("Siguiente"),
                    previous: t("Anterior"),
                    today: t("Hoy"),
                    month: t("Mes"),
                    week: t("Semana"),
                    day: t("Día")
                }}
                views={['week', 'day']}
                style={{ height: '100%' }}
            />
            <Fab
                color="primary"
                aria-label="add"
                sx={{
                    position: 'fixed',
                    bottom: '20px',
                    right: '20px',
                    width: '50px !important',
                    height: '50px !important',
                    minHeight: '50px !important',
                    borderRadius: '50%',
                    zIndex: 1000,
                }}
                onClick={openEditAddModal}
                >
                {getIcon('fav_add')}
            </Fab>
            {isModalOpen && selectedEvent && (
                <DeleteEventModal
                    locationId={locationId}
                    event={selectedEvent}
                    open={isModalOpen}
                    onClose={closeModal}
                    onDelete={async () => {
                        // Refrescar los eventos después de la eliminación
                        await fetchSchedule();
                    }}
                />
            )}
            {isEditAddModalOpen && (
                <EditAddEventModal
                    locationId={locationId}
                    open={isEditAddModalOpen}
                    onClose={closeEditAddModal}
                    users={users}
                    event={selectedEvent}
                    onSave={async () => {
                        // Refrescar los eventos después de la eliminación
                        await fetchSchedule();
                    }}
                />
            )}
        </>        
    );
};

export default StaffCalendar;
